import Highcharts from "highcharts";
import { DateTime } from "luxon";
import { makeAutoObservable } from "mobx";
import { getEnv, getRoot } from "mobx-easy";
import { StoreState } from "../../../enum/StoreState";
import { RootEnv } from "../../setup/create-store";
import {
  IEnvironmentalVariable,
  IGanttChartData,
  IGanttChartSerieEnvironmentalVariable,
  IGanttChartSerieTemporal,
  IInsightChangeForUpdate,
  IStation,
  ProbabilityType,
  RiskLevel,
} from "./oceanWeatherIntelligenceModel";
import i18n from "../../../i18n";
import axios from "axios";
import { IGetGanttChartDataParams } from "../../../services/OceanWeatherIntelligenceService";
import RootStore from "stores/root-store";
import { formatChartValue } from "../../../util/formatChartValue";
import { ProductAnalyticsEvents } from "enum/ProductAnalyticsEvents";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import { LatLngLiteral } from "leaflet";

export default class OceanWeatherIntelligenceStore {
  ganttChartData: IGanttChartData[] = [
    { name: "", data: [], start: 0, end: 0, timezoneOffset: 0, errors: 0 },
  ];

  stations: IStation[] = [];

  state: string = StoreState.DONE;

  messageError: string = "";

  changeForUpdate: IInsightChangeForUpdate = {
    update: false,
    expiration: 0,
  };

  mapCenterLatitude: number = 0;

  mapCenterLongitude: number = 0;

  environmentalVariables: IEnvironmentalVariable[] = [];

  selectForecastDateAvailable: DateTime | undefined;

  actionCallGanttChart: boolean = false;

  activeTab: number = 0;

  timezoneOffset: number = 0;

  allowedEnvironmentalVariablesForProbability = [
    EnvironmentalVariables.WIND_VEL,
    EnvironmentalVariables.WIND_VEL_80M,
    EnvironmentalVariables.WIND_VEL_100M,
    EnvironmentalVariables.WIND_DIR,
    EnvironmentalVariables.WIND_DIR_80M,
    EnvironmentalVariables.WIND_DIR_100M,
    EnvironmentalVariables.GUST,
    EnvironmentalVariables.GUST_80M,
    EnvironmentalVariables.GUST_100M,
  ];

  // isGanttProbability = true;

  // isSwitchProbabilityClicked = false;

  showProbabilisticFeedback = false;

  selectedGeofence: LatLngLiteral[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  setGanttChartData(values: IGanttChartData) {
    this.ganttChartData = [values];
  }

  setStations(values: IStation[]) {
    this.stations = values;
  }

  setChangeForUpdate(value: IInsightChangeForUpdate) {
    this.changeForUpdate = value;
  }

  getStationForLocation(stationId: number, regionId: number) {
    return this.stations.find(
      (station) =>
        station.station_id === stationId && station.region_id === regionId,
    ) as IStation;
  }

  setState(value: StoreState) {
    this.state = value;
  }

  setMessageError(value: string) {
    this.messageError = value;
  }

  setSelectedGeofence(value: LatLngLiteral[][] | LatLngLiteral[]) {
    if (!value || value.length === 0) {
      this.selectedGeofence = [];
      return;
    }

    if (Array.isArray(value[0])) {
      this.selectedGeofence = (value as LatLngLiteral[][]).flat();
    } else {
      this.selectedGeofence = value as LatLngLiteral[];
    }
  }

  setEnviromentalVariables(values: IEnvironmentalVariable[]) {
    this.environmentalVariables = values;
  }

  setMapCenter(): void {
    for (let i = 0; i < this.stations.length; i++) {
      if (
        this.stations[i].station_lat !== null &&
        this.stations[i].station_lon !== null
      ) {
        this.mapCenterLatitude = this.stations[i].station_lat;
        this.mapCenterLongitude = this.stations[i].station_lon;
        break;
      }
    }
  }

  setSelectForecastDateAvailable(values: DateTime) {
    this.selectForecastDateAvailable = values;
  }

  setActionCallGanttChart() {
    this.actionCallGanttChart = !this.actionCallGanttChart;
  }

  setActiveTab(value: number) {
    this.activeTab = value;
  }

  setTimezoneOffset(value: number) {
    this.timezoneOffset = value;
  }

  // setIsGanttProbability(value: boolean) {
  //   this.isGanttProbability = value;
  // }

  // setIsSwitchProbabilityClicked(value: boolean) {
  //   this.isSwitchProbabilityClicked = value;
  // }

  setShowProbabilisticFeedback(value: boolean) {
    this.showProbabilisticFeedback = value;
  }

  clearSelectForecastDateAvailable() {
    this.selectForecastDateAvailable = undefined;
  }

  reset() {
    this.setGanttChartData({
      name: "",
      data: [],
      start: 0,
      end: 0,
      timezoneOffset: 0,
      errors: 0,
    });
    this.setState(StoreState.DONE);
    this.setEnviromentalVariables([]);
    this.setTimezoneOffset(0);
    // this.setIsGanttProbability(true);
    // this.setIsSwitchProbabilityClicked(false);
    this.setShowProbabilisticFeedback(false);
  }

  async getGanttChartDataApi({
    operationRuleId,
    cancelToken,
    create_at,
  }: IGetGanttChartDataParams) {
    this.setMessageError("");
    const { oceanWeatherIntelligenceService } = getEnv<RootEnv>();
    this.setGanttChartData({
      name: "",
      data: [],
      start: 0,
      end: 0,
      timezoneOffset: 0,
      errors: 0,
    });
    this.setState(StoreState.PEDDING);
    try {
      const chartData: IGanttChartData =
        await oceanWeatherIntelligenceService.getGanttChartData({
          operationRuleId,
          create_at: create_at || this.selectForecastDateAvailable?.toISO(),
          cancelToken,
          // isGanttProbability: this.isGanttProbability
        });

      const hasAnyOrder = chartData?.data?.some(
        (item) => item.order !== null && item.order !== undefined,
      );

      if (hasAnyOrder) {
        chartData.data = this.sortGanttChartData(chartData.data);
      }

      this.setGanttChartData(chartData);

      this.setTimezoneOffset(chartData.timezoneOffset);

      if (chartData.errors > 0) {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.errorGeneratingInsight"),
        );
      }
      this.setState(StoreState.DONE);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("axios request cancelled");
      } else {
        this.setState(StoreState.ERROR);
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.errorGetChartData"),
        );
      }
    }
  }

  async getStationForLocationAPI(macroRegionId: number) {
    this.setState(StoreState.PEDDING);
    const { oceanWeatherIntelligenceService } = getEnv<RootEnv>();
    this.setStations([]);
    const stationsResult =
      await oceanWeatherIntelligenceService.getStationForLocation(
        macroRegionId,
      );
    this.setStations(stationsResult);
    this.setMapCenter();
    this.setState(StoreState.DONE);
    return stationsResult;
  }

  async getEnvironmentalVariableForStationAPI(
    stationId: number,
    macroRegionId: number,
  ) {
    const { oceanWeatherIntelligenceService } = getEnv<RootEnv>();
    this.setState(StoreState.PEDDING);
    this.setEnviromentalVariables([]);
    this.setMessageError("");

    // list com nome interno das variaveis ambientais desejadas
    const mock = [
      "water_level",
      "current_vel",
      "current_dir",
      "wave_height",
      "wave_dir",
      "peak_period",
      "mean_period",
      "wave_power",
      "wave_height_max",
      "wave_height_sea",
      "wave_dir_sea",
      "peak_period_sea",
      "mean_period_sea",
      "wave_height_swell",
      "wave_dir_swell",
      "peak_period_swell",
      "mean_period_swell",
      "wind_vel",
      "wind_dir",
      "wind_vel_80m",
      "wind_dir_80m",
      "wind_vel_100m",
      "wind_dir_100m",
      "gust_smooth",
      "gust",
      "gust_80m",
      "gust_100m",
      "precip",
      "rel_humidity",
      "visibility",
      "visibility_minimum_20km",
      "visibility_maximum_20km",
      "visibility_probability_600m",
      "visibility_probability_700m",
      "visibility_probability_1000m",
      "visibility_probability_1500m",
      "visibility_probability_2000m",
      "visibility_probability_3000m",
      "visibility_probability_5000m",
      "slp",
      "dew_point",
      "temp_air",
      "total_cloud_cover",
      "lightning_density",
      "precip_min_20km",
      "precip_max_20km",
      "precip_probability",
      "precip_probability_35km",
      "precip_accum_12h",
      "precip_accum_24h",
      "precip_accum_48h",
      "precip_accum_72h",
      "precip_accum_96h",
      "precip_accum_120h",
      "landslide_probability_risk",
      "wave_energy_directional_swell_01",
      "wave_energy_directional_swell_02",
      "wave_energy_directional_swell_03",
      "wave_dir_spread_factor_sea_01",
      "wave_dir_spread_factor_sea_02",
      "precipitation_type",
      "snowfall_smooth_rate",
      "icing_probability",
    ];

    try {
      const variablesResult: IEnvironmentalVariable[] =
        await oceanWeatherIntelligenceService.getEnvironmentalVariableForStation(
          stationId,
          macroRegionId,
        );

      const variableFilter: IEnvironmentalVariable[] = [];

      for (const value of mock) {
        const foundVariable = variablesResult.find(
          (environmentalVariable) =>
            environmentalVariable.internal_name === value,
        );
        if (foundVariable) {
          if (foundVariable.internal_name.indexOf("_80m") !== -1) {
            // SLA for wind 50m acu region
            foundVariable.name = foundVariable.name.replace("80", "50");
          }
          variableFilter.push(foundVariable);
        }
      }
      this.setEnviromentalVariables(variableFilter);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.environmentalVariablesNotFound"),
      );
    }
    this.setState(StoreState.DONE);
  }

  renderMinMax(min: string, max: string, units: string) {
    return (
      "<div style='display:flex; align-item: center; justify-content: space-between; gap: 10px; white-space: nowrap; word-break: normal;'>" +
      "<div>" +
      "<span>min: </span>" +
      "<span>" +
      min +
      units +
      "</span>" +
      "</div>" +
      "<div>" +
      "<span>max: </span>" +
      "<span>" +
      max +
      units +
      "</span>" +
      "</div>" +
      "</div>"
    );
  }

  /**
   * Single source of truth for how an environmental variable reads in the Gantt
   * tooltip: the 50m wind SLA rename, m2 in scientific-capable notation,
   * knots/seconds abbreviated, and degrees rendered without a min/max pair.
   * Returns undefined for an incomplete variable, which the caller skips.
   */
  private formatEnvironmentalVariable(
    item: IGanttChartSerieEnvironmentalVariable,
  ) {
    let { environmentalVariable, min, max, units } = item;

    // SLA for wind 50m acu region
    if (environmentalVariable.indexOf("80") !== -1) {
      environmentalVariable = environmentalVariable.replace("80", "50");
    }

    if (min === undefined || max === undefined || !units) return undefined;

    const isM2 = units === "m2";
    const minFormatted = isM2 ? formatChartValue(min, 3) : min.toFixed(1);
    const maxFormatted = isM2 ? formatChartValue(max, 3) : max.toFixed(1);
    const isDegrees = units === "degrees";

    if (units === "knots") {
      units = "kt";
    }

    if (units === "seconds") {
      units = "s";
    }

    return {
      label: environmentalVariable,
      min: minFormatted,
      max: maxFormatted,
      units,
      showMinMax: !isDegrees,
    };
  }

  renderEnvironmentalVariable(
    environmentalVariables: IGanttChartSerieEnvironmentalVariable[],
  ) {
    let newTable = "";

    for (const item of environmentalVariables) {
      const formatted = this.formatEnvironmentalVariable(item);
      if (!formatted) continue;

      const _renderMinMax = formatted.showMinMax
        ? this.renderMinMax(formatted.min, formatted.max, formatted.units)
        : "";

      newTable +=
        "<div style='display:flex; align-items: center; word-break: normal;'>" +
        "<img src='/triangle-alert.svg' alt='Ícone' width='16' height='16' style='flex-shrink: 0;' />" +
        "<div style='display:flex; flex-direction: column; padding-left: 8px; padding-right: 16px; min-width: 0;'>" +
        "<b style='color:#2563EB; font-size: 14px; word-wrap: break-word; overflow-wrap: break-word;'>" +
        formatted.label +
        "</b>" +
        "<div style='display:flex; align-items:center; font-size: 14px;'>" +
        _renderMinMax +
        "</div>" +
        "</div>" +
        "</div>";
    }

    return newTable;
  }

  /** The impact line of the tooltip card: the share of the area impacted. */
  private renderImpactSummary(percentageArea: number) {
    // UMA casa decimal, e no idioma do usuario (workspace#385, 09/09/2026).
    //
    // ⚠️ O par disto vive no aurora, no skeleton ICU `::.#` da mensagem
    // `locationPanel.tooltip.areaImpacted` (i4sea-aurora-ui 694d917e4). O MESMO evento
    // aparece nas duas telas e esta entrega existe para elas concordarem, entao as duas
    // formatacoes andam juntas: `maximumFractionDigits` aqui e o `.#` de la -- uma casa
    // QUANDO houver, nunca zero a direita, para 100% nao virar "100,0%".
    //
    // 📖 `toLocaleString` e nao `Math.round(x * 10) / 10`: o arredondamento manual
    // acerta o digito e erra a PONTUACAO, porque numero vira string sem locale. O
    // aurora lia "32,5%" e este gantt "32.5%" para o mesmo dado -- o mesmo numero
    // escrito de dois jeitos e divergencia igual, so que mais dificil de notar.
    //
    // 📏 Com uma casa a fatia pequena sobrevive: 0.397% le "0,4%" e continua
    // distinguivel da area avaliada e de fato NAO impactada, que e um estado com
    // significado proprio aqui. Arredondada a inteiro ela virava "0%" e as duas coisas
    // se confundiam.
    //
    // O arredondamento e de APRESENTACAO nos dois lados: o payload mantem a precisao
    // que o motor calculou, entao realinhar de novo nao exige tocar em backend.
    const percentual = percentageArea.toLocaleString(
      i18n.resolvedLanguage || i18n.language || "pt-BR",
      { maximumFractionDigits: 1 },
    );

    return (
      `<strong>${percentual}%</strong> ` +
      i18n.t("oceanWeatherIntelligence.impactedArea")
    );
  }

  renderRisk(risk: RiskLevel) {
    const riskToColor: Record<RiskLevel, string> = {
      [RiskLevel.LOW]: "#6e6e75",
      [RiskLevel.MODERATE]: "#309E3A",
      [RiskLevel.HIGH]: "#EAB308",
      [RiskLevel.EXTREME]: "#DC2626",
    };
    const riskColor = riskToColor[risk] || "#309E3A";
    const riskLabel = this.getRiskLevelLabel(risk);
    let newTable = "";

    newTable +=
      "<div style='display: flex; align-items: center; justify-content: space-between; gap: 24px; word-break: normal;'>" +
      `<span style='font-size: 14px; color: #71717A;'>${i18n.t(
        "oceanWeatherIntelligence.riskMatrix",
      )}:</span>` +
      `<div style='display: flex; align-items: center; justify-content: center;background-color: ${riskColor}; color: #fff; font-size: 14px; padding: 6px 8px; border-radius: 8px;'>
      ${riskLabel}
      </div>` +
      "</div>";

    return newTable;
  }

  renderRiskBar(risk: RiskLevel) {
    const riskToIndexMap: Record<RiskLevel, number> = {
      [RiskLevel.LOW]: 0,
      [RiskLevel.MODERATE]: 1,
      [RiskLevel.HIGH]: 2,
      [RiskLevel.EXTREME]: 3,
    };

    const markerIndex = riskToIndexMap[risk] ?? 1;
    const markerLeft = markerIndex * 25 + 12.5;

    const colors = ["#6e6e75", "#309E3A", "#EAB308", "#DC2626"];

    let newTable = `
    <div style="position: relative; display: flex; justify-content: center; width: 100%; height: 19px; align-items: center;">
      <div style="display: flex; width: 100%; height: 14px;">`;

    colors.forEach((color, index) => {
      newTable += `
        <div key={${index}} style="
          background-color: ${color};
          width: 25%;
          height: 100%;
        "></div>`;
    });

    newTable += `
      </div>
      <div style="
        position: absolute;
        left: ${markerLeft}%;
        top: 0;
        transform: translateX(-50%);
        width: 3px;
        height: 19px;
        background-color: black;
        z-index: 2;
      "></div>
    </div>
  `;

    return newTable;
  }

  renderProbabilityAndSeverity(porcentage: string, severity: string) {
    let newTable = "";

    newTable +=
      "<div style='display: flex;justify-content: space-between; color: black; gap: 24px; color: #71717A;'>" +
      `<span> ${i18n.t(
        "oceanWeatherIntelligence.probability",
      )} - <strong>${porcentage}</strong></span>` +
      `<span> ${i18n.t(
        "oceanWeatherIntelligence.severity",
      )} - <strong>${severity}</strong></span>` +
      "</div>";

    return newTable;
  }

  /**
   * Truncates text to fit within a tooltip.
   * Forces line breaks on long words and limits to ~3 lines with ellipsis.
   * @param text - The text to truncate
   * @param maxChars - Max total visible characters before adding ellipsis
   * @param lineWidth - Approximate chars per line to force-break long words
   */
  private truncateTooltipText(
    text: string,
    maxChars = 120,
    lineWidth = 40,
  ): string {
    if (!text) return text;

    // First, truncate to max chars and add ellipsis if needed
    let truncated = text;
    let hasEllipsis = false;
    if (truncated.length > maxChars) {
      truncated = truncated.slice(0, maxChars);
      hasEllipsis = true;
    }

    // Then, force-break any long unbroken sequences by inserting <wbr> tags
    // Split by spaces, break long words, rejoin
    const words = truncated.split(/(\s+)/);
    const result = words
      .map((word) => {
        if (word.length > lineWidth && word.trim().length > 0) {
          const chunks: string[] = [];
          for (let i = 0; i < word.length; i += lineWidth) {
            chunks.push(word.slice(i, i + lineWidth));
          }
          return chunks.join("<wbr>");
        }
        return word;
      })
      .join("");

    return result + (hasEllipsis ? "..." : "");
  }

  ganttChartFormatTooltip(point: any) {
    const chartData = point.point.series.userOptions as IGanttChartData;

    const parentData = chartData.data.find(
      (data) => data.id === point.point.parent,
    );
    const isProbability = point.point.isProbability;
    const probabilityType = point.point.probabilityType;
    const probability = point.point.probability;
    const environmentalVariables = point.point?.environmentalVariables;
    const severityId = point.point?.severityId;
    const risk = point.point?.risk;
    const resume = isProbability ? parentData?.resume : point.point?.resume;
    const name =
      isProbability && probabilityType === ProbabilityType.PERCENTAGE
        ? parentData?.name
        : point.point.name;
    const showProbability =
      isProbability && probabilityType === ProbabilityType.PERCENTAGE;
    const showEnvironmentalVariables = environmentalVariables?.length;
    const percentageArea = point.point.percentage_area;
    const locationName = point.point.location_name;
    const truncatedName = this.truncateTooltipText(name, 80, 35);
    const truncatedResume = resume
      ? this.truncateTooltipText(resume, 120, 40)
      : "";

    let ret =
      "<div style='display: flex; flex-direction: column; gap: 16px; padding: 10px; min-width: 300px; max-width: 360px; box-sizing: border-box;'>";

    ret += `<div style='display: flex; justify-content: space-between; align-items: center; gap: 18px; min-width: 0;'>
      <h1 style="font-weight: bold; font-size: 16px; min-width: 0; word-break: break-all; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; line-height: 1.3;">${truncatedName}</h1>`;

    ret += "</div>";

    if (resume) {
      ret += `<div>
        <h2 style="font-size: 12px;">${i18n.t(
          "oceanWeatherIntelligence.summary",
        )}:</h2>
        <p style="font-size: 12px; word-break: break-all; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; line-height: 1.4;">${truncatedResume}</p>
      </div>`;
    }

    ret +=
      "<div style='width: 100%; height: 1px; background-color: #E4E4E7'></div>" +
      "<div style='display: flex; align-items:center; justify-content: space-between; font-size: 12px; gap: 10px; color: #52525B; word-break: normal; white-space: nowrap;'>" +
      `<div style='display: flex; flex-direction: column;  align-items: center; color: #52525B;'>
        <span style='font-size: 18px; font-weight: 600;'>${Highcharts.dateFormat(
          "%H:%M",
          point.x,
        )}</span>
        <span style='font-size: 14px; font-weight: 400;'>${Highcharts.dateFormat(
          "%d/%m",
          point.x,
        )}</span>
       </div>

       <div style='display: flex; flex-direction: column; align-items: center;'>
        <img src="/arrow-right.svg" alt="Ícone" width="40" height="24" />
        <span>${i18n.t("oceanWeatherIntelligence.to")}</span>
      </div>
        
        <div style='display: flex; flex-direction: column;  align-items: center; color: #52525B;'>
        <span style='font-size: 18px; font-weight: 600;'>${Highcharts.dateFormat(
          "%H:%M",
          point.x2,
        )}</span>
        <span style='font-size: 14px; font-weight: 400;'>${Highcharts.dateFormat(
          "%d/%m",
          point.x2,
        )}</span>
       </div> ` +
      "</div>";

    // Not a truthy check: a legitimate 0% is an answer, not an absence, and
    // swallowing it would hide valid data.
    if (percentageArea !== undefined && percentageArea !== null) {
      const impactSummary = this.renderImpactSummary(percentageArea);
      ret += `<div style='display: flex; padding: 10px 24px; align-items: center; gap: 16px; align-self: stretch; border-radius: 8px; border: 1px solid var(--primary-800, #0884CC); background: var(--alpha-primary-200, rgba(183, 227, 252, 0.32));'>
        <img src="/vector-square.png" alt="" width="16" height="16" style="flex-shrink: 0;" />
        <div style="display: flex; flex-direction: column; flex: 1;">
          ${
            // ⚠️ Interpolar direto imprimia a palavra "undefined" na tela quando o campo
            // vinha ausente -- 📏 36 barras do painel 673 em 08/09/2026, porque o backend
            // nao preenchia `location_name` neste caminho (corrigido no i4cast-workspace,
            // commit a145e96).
            //
            // A causa foi consertada la, e ainda assim a guarda fica: um campo ausente deve
            // DESAPARECER, nunca virar a palavra "undefined" na cara do operador. O resumo
            // de impacto, que e a informacao substantiva do bloco, segue sozinho.
            locationName
              ? `<span style="color: var(--primary-800, #0884CC); font-size: 14px; font-style: normal; font-weight: 400; line-height: 24px;">${locationName}</span>`
              : ""
          }
          <span style="color: var(--blue-gray-1000, #3E4F61); text-align: left; font-size: 12px; font-style: normal; font-weight: 400; line-height: 24px;">${impactSummary}</span>
        </div>
      </div>`;
    }

    if (showProbability || showEnvironmentalVariables) {
      ret +=
        "<div style='width: 100%; height: 1px; background-color: #E4E4E7'></div>";
    }

    if (showEnvironmentalVariables) {
      ret += this.renderEnvironmentalVariable(environmentalVariables);
    }

    if (showProbability) {
      const severityLabel = this.getSeverityLabel(severityId);
      ret += `<div style='display: flex; flex-direction: column; gap: 8px;'>
       ${this.renderRisk(risk as RiskLevel.EXTREME)}
       ${this.renderRiskBar(risk as RiskLevel.EXTREME)}
      </div>`;
      ret += this.renderProbabilityAndSeverity(probability, severityLabel);
    }

    ret += "</div>";

    return ret;
  }

  getProbabilityConcept(probability: string): string {
    switch (probability) {
      case "10%":
        return i18n.t("oceanWeatherIntelligence.rare");
      case "25%":
        return i18n.t("oceanWeatherIntelligence.low");
      case "50%":
        return i18n.t("oceanWeatherIntelligence.medium");
      case "75%":
        return i18n.t("oceanWeatherIntelligence.high");
      case "90%":
        return i18n.t("oceanWeatherIntelligence.almostCertain");
      default:
        return i18n.t("oceanWeatherIntelligence.medium");
    }
  }

  getRiskLevelLabel(risk: RiskLevel): string {
    switch (risk) {
      case RiskLevel.EXTREME:
        return i18n.t("oceanWeatherIntelligence.riskExtreme");
      case RiskLevel.HIGH:
        return i18n.t("oceanWeatherIntelligence.riskHigh");
      case RiskLevel.MODERATE:
        return i18n.t("oceanWeatherIntelligence.riskModerate");
      case RiskLevel.LOW:
        return i18n.t("oceanWeatherIntelligence.riskLow");
      default:
        return i18n.t("oceanWeatherIntelligence.riskModerate");
    }
  }

  getRiskLevelColor(risk: RiskLevel): string {
    switch (risk) {
      case RiskLevel.EXTREME:
        return "#DC2626";
      case RiskLevel.HIGH:
        return "#CA8A04";
      case RiskLevel.MODERATE:
        return "#16A34A";
      case RiskLevel.LOW:
        return "#52525B";
      default:
        return "#16A34A";
    }
  }

  getSeverityLabel(severity: number): string {
    switch (severity) {
      case 1:
        return i18n.t(
          "oceanWeatherIntelligence.operationRuleLocationInsight.noImpact",
        );
      case 2:
        return i18n.t(
          "oceanWeatherIntelligence.operationRuleLocationInsight.minor",
        );
      case 3:
        return i18n.t(
          "oceanWeatherIntelligence.operationRuleLocationInsight.moderate",
        );
      case 4:
        return i18n.t(
          "oceanWeatherIntelligence.operationRuleLocationInsight.major",
        );
      case 5:
        return i18n.t(
          "oceanWeatherIntelligence.operationRuleLocationInsight.severe",
        );
      default:
        return i18n.t(
          "oceanWeatherIntelligence.operationRuleLocationInsight.moderate",
        );
    }
  }

  processDateCurrent(value: DateTime) {
    const {
      dataStores: { userConfigStore },
    } = getRoot<RootStore>();

    const timezoneOffset = DateTime.now().setZone(
      userConfigStore.timezone,
    ).offset;
    const timezoneOffsetLocal = DateTime.now().offset;

    const diff = timezoneOffset - timezoneOffsetLocal;

    const now = DateTime.now()
      .plus({ minutes: diff })
      .setZone(userConfigStore.timezone);

    if (value.minute > 45) {
      return now
        .set({
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .plus({ hours: 1 })
        .toJSDate();
    } else if (value.minute > 30) {
      return now
        .set({
          minute: 45,
          second: 0,
          millisecond: 0,
        })
        .toJSDate();
    } else if (value.minute > 15) {
      return now
        .set({
          minute: 30,
          second: 0,
          millisecond: 0,
        })
        .toJSDate();
    } else {
      return now
        .set({
          minute: 15,
          second: 0,
          millisecond: 0,
        })
        .toJSDate();
    }
  }

  needUpdateGanttChart() {
    if (
      (this.ganttChartData?.length && this.ganttChartData[0].name === "") ||
      this.changeForUpdate.update ||
      this.changeForUpdate.expiration <= DateTime.local().toMillis()
    ) {
      return true;
    }
    return false;
  }

  async addProductAnalyticsEvent(
    event: ProductAnalyticsEvents,
    eventProperty: Record<string, any>,
  ) {
    const { logServices } = getEnv<RootEnv>();
    try {
      await logServices.addProductAnalyticsEvent(event, eventProperty);
    } catch (error) {
      console.error(`Error while call addProductAnalytics event`);
    }
  }

  filterEnvironmentalVariablesForProbability(
    environmentalVariables: IEnvironmentalVariable[],
  ): IEnvironmentalVariable[] {
    return environmentalVariables.filter((variable) =>
      this.allowedEnvironmentalVariablesForProbability.includes(
        variable.internal_name as EnvironmentalVariables,
      ),
    );
  }

  sortGanttChartData(
    data: IGanttChartSerieTemporal[],
  ): IGanttChartSerieTemporal[] {
    const isInsightLike = (x: IGanttChartSerieTemporal) =>
      Boolean(x?.isInsight) ||
      (typeof x?.probability === "string" &&
        x.probability.trim().toLowerCase() === "total");

    return [...data].sort((a, b) => {
      const aLoc = Boolean(a?.isLocation);
      const bLoc = Boolean(b?.isLocation);
      if (aLoc && !bLoc) return -1;
      if (!aLoc && bLoc) return 1;

      const aInsight = isInsightLike(a);
      const bInsight = isInsightLike(b);
      if (aInsight && !bInsight) return 1;
      if (!aInsight && bInsight) return -1;

      const orderA = Number.isFinite(a?.order as number)
        ? (a.order as number)
        : Infinity;
      const orderB = Number.isFinite(b?.order as number)
        ? (b.order as number)
        : Infinity;
      return orderA - orderB;
    });
  }
}
